import React, {useEffect, useReducer} from 'react';
import {Box} from '@mui/material';
import {useIntl} from 'react-intl';
import Selector from '../../../../../../components/shared/nextgen/selector';
import {FilterActionType, FilterChangeAction, FilterState} from '../../../../../../components/Schedule/header/components/filter/types';
import {REmployees} from '../../../../../../features/roles/types';
import {PIdNameModel} from '../../../../../../features/projects/types';
import {Lx} from '../../../../../../i18n/consts';
import styles from './mobile-filter-sheet.module.scss';
import {AutoCompleteItemValue} from '../../../../../../components/shared/nextgen/selector/types';
import TypesSelector from '../../../../../../components/Schedule/header/components/types-selector';
import {TiminatorAbsenceType, TiminatorType} from '../../../../../../features/types/types';
import SearchInput from '../../../../../../components/shared/search-input';
import {ActionCancelButtonsArea} from '../../../../../../components/shared/action-cancel-buttons-area';

interface Props {
    open: boolean;
    onClose: () => void;
    onApply: (filter: FilterState) => Promise<void> | void;
    onClear: () => void;
    search: string;
    onSearchChange: (value: string) => void;
    filters: FilterState;
    employees: REmployees[];
    projects: PIdNameModel[];
    projectTypes: TiminatorType[];
    shiftTypes: TiminatorType[];
    absenceTypes: TiminatorAbsenceType[];
    statuses: AutoCompleteItemValue[];
    disabled?: boolean;
    disableEmployeesFilter?: boolean;
}

interface ReducerState {
    draft: FilterState;
    current: FilterState;
}

const createEmptyFilterState = (): FilterState => ({
    selectedEmployees: [],
    selectedProjects: [],
    selectedProjectTypes: [],
    selectedShiftTypes: [],
    selectedAbsenceTypes: [],
    selectedStatuses: [],
    showAbsences: true,
});

const applyChange = (type: FilterActionType, payload: any, state: FilterState): FilterState => {
    const next = {...state};
    switch (type) {
        case 'EMPLOYEES_UPDATE':
            next.selectedEmployees = payload ?? [];
            break;
        case 'PROJECTS_UPDATE':
            next.selectedProjects = payload ?? [];
            break;
        case 'PROJECTTYPE_UPDATE':
            next.selectedProjectTypes = payload ?? [];
            break;
        case 'SHIFTTYPE_UPDATE':
            next.selectedShiftTypes = payload ?? [];
            break;
        case 'ABSENCETYPE_UPDATE':
            next.selectedAbsenceTypes = payload ?? [];
            break;
        case 'STATUSES_UPDATE':
            next.selectedStatuses = payload ?? [];
            break;
        case 'SHOW_ABSENCES_UPDATE':
            next.showAbsences = typeof payload === 'boolean' ? payload : true;
            break;
        default:
            break;
    }
    return next;
}

const initialReducerState: ReducerState = {
    current: createEmptyFilterState(),
    draft: createEmptyFilterState(),
};

function reducer(state: ReducerState, action: Partial<FilterChangeAction>): ReducerState {
    const nextState = {...state};

    switch (action.action) {
        case 'HYDRATE': {
            const incoming = (action.payload as FilterState) ?? createEmptyFilterState();
            nextState.current = {...incoming};
            nextState.draft = {...incoming};
            break;
        }
        case 'APPLY':
            nextState.current = {...state.draft};
            break;
        case 'CANCEL':
            nextState.draft = {...state.current};
            break;
        case 'RESET':
            nextState.draft = createEmptyFilterState();
            break;
        default:
            nextState.draft = applyChange(action.action as FilterActionType, (action as FilterChangeAction).payload, nextState.draft);
            break;
    }

    return nextState;
}

const TimeSheetMobileFilterSheet = ({
    open,
    onClose,
    onApply,
    onClear,
    search,
    onSearchChange,
    filters,
    employees,
    projects,
    projectTypes,
    shiftTypes,
    absenceTypes,
    statuses,
    disabled,
    disableEmployeesFilter,
}: Props) => {
    const intl = useIntl();
    const [filterState, dispatch] = useReducer(reducer, initialReducerState);

    useEffect(() => {
        if (open) {
            dispatch({ action: 'HYDRATE', payload: filters });
        } else {
            dispatch({ action: 'CANCEL' });
        }
    }, [open, filters]);

    const handleApply = () => {
        dispatch({ action: 'APPLY' });
        const result = onApply(filterState.draft);
        if (result && typeof (result as Promise<void>).then === 'function') {
            (result as Promise<void>).finally(onClose);
        } else {
            onClose();
        }
    };

    const handleClearFilters = () => {
        dispatch({ action: 'RESET' });
        onClear();
    };

    if (!open || disabled) {
        return null;
    }

    const employeeItems = employees.map((employee) => ({
        id: employee.id,
        name: employee.name,
        originalId: employee.id,
    }));

    const projectItems = projects.map((project) => ({
        id: project.id,
        name: project.name,
        originalId: project.id,
    }));

    const projectTypeItems = projectTypes.map((projectType) => ({
        id: projectType.id,
        name: projectType.name,
        originalId: projectType.id,
    }));

    const statusItems = statuses.map((status, index) => ({
        ...status,
        id: status.id ?? index,
        name: intl.formatMessage({ id: status.name }),
        originalId: status.originalId ?? status.id,
    }));

    return (
        <div className={styles.sheet}>
            <div className={styles.content}>
                <SearchInput
                    searchValue={search ?? ''}
                    onChange={onSearchChange}
                    width='100%'
                    disabled={disabled}
                />

                {!disableEmployeesFilter && (
                    <div className={styles.selectorBlock}>
                        <Selector
                            id={'timesheet-mobile-filter-employees'}
                            label={Lx.General.EMPLOYEES}
                            items={employeeItems}
                            name={Lx.General.EMPLOYEES}
                            selected={filterState.draft.selectedEmployees}
                            setSelectedValues={(values) => dispatch({ action: 'EMPLOYEES_UPDATE', payload: values })}
                            intl={intl}
                            maxWidth={1000}
                            disabled={disabled}
                        />
                    </div>
                )}

                <div className={styles.selectorBlock}>
                    <Selector
                        id={'timesheet-mobile-filter-projects'}
                        label={Lx.General.PROJECTS}
                        items={projectItems}
                        name={Lx.General.PROJECTS}
                        selected={filterState.draft.selectedProjects}
                        setSelectedValues={(values) => dispatch({ action: 'PROJECTS_UPDATE', payload: values })}
                        intl={intl}
                        maxWidth={1000}
                        disabled={disabled}
                    />
                </div>

                <div className={styles.selectorBlock}>
                    <Selector
                        id={'timesheet-mobile-filter-project-types'}
                        label={Lx.General.PROJECT_TYPES}
                        items={projectTypeItems}
                        name={Lx.General.PROJECT_TYPES}
                        selected={filterState.draft.selectedProjectTypes}
                        setSelectedValues={(values) => dispatch({ action: 'PROJECTTYPE_UPDATE', payload: values })}
                        intl={intl}
                        maxWidth={1000}
                        disabled={disabled}
                    />
                </div>

                <div className={styles.selectorBlock}>
                    <TypesSelector
                        selectedShiftTypes={filterState.draft.selectedShiftTypes}
                        selectedAbsenceTypes={filterState.draft.selectedAbsenceTypes}
                        shiftTypes={shiftTypes}
                        absenceTypes={absenceTypes}
                        onShiftTypesSelected={(values) => dispatch({ action: 'SHIFTTYPE_UPDATE', payload: values })}
                        onAbsenceTypesSelected={(values) => dispatch({ action: 'ABSENCETYPE_UPDATE', payload: values })}
                        intl={intl}
                        disabled={disabled}
                    />
                </div>

                <div className={styles.selectorBlock}>
                    <Selector
                        id={'timesheet-mobile-filter-statuses'}
                        label={Lx.General.STATUS}
                        items={statusItems}
                        name={Lx.General.STATUS}
                        selected={filterState.draft.selectedStatuses}
                        setSelectedValues={(values) => dispatch({ action: 'STATUSES_UPDATE', payload: values })}
                        intl={intl}
                        maxWidth={1000}
                        disabled={disabled}
                    />
                </div>

            </div>
            <Box className={styles.footer}>
                <ActionCancelButtonsArea
                    idActionLocalization={Lx.General.APPLY}
                    idCancelLocalization={Lx.General.RESET_FILTER}
                    onCancel={handleClearFilters}
                    onAction={handleApply}
                    disabled={disabled}
                />
            </Box>
        </div>
    );
};

export default TimeSheetMobileFilterSheet;
